import { Controller } from '@hotwired/stimulus';
import Sortable from 'sortablejs';

/**
 * Trades Config Controller
 *
 * Manages the trades configuration UI on the Settings page.
 * Handles drag-and-drop reordering, add/remove/restore rows,
 * counter badge updates, max-60 enforcement, and form serialization.
 *
 * Does NOT auto-save — all changes are deferred until form submit.
 *
 * Targets:
 * - category: <div> column wrapper per category (has data-category)
 * - counter: Badge wrapper span per category (updated on add/remove/restore)
 * - list: Scrollable container of trade rows per category
 * - addButton: "+ Ajouter un lot" button per category (has data-category)
 * - maxMessage: "Limite de 60" helper text per category
 * - hiddenField: Hidden input per category for JSON serialization (has data-category)
 * - item: Individual trade row (has data-deleted)
 * - input: Text input within a trade row
 * - deleteButton: Delete button within a trade row
 * - restoreButton: Restore button within a trade row (hidden by default)
 *
 * Actions:
 * - addTrade: Inserts a new trade row at the bottom of the clicked category
 * - deleteTrade: Soft-deletes a trade row (strikethrough + opacity)
 * - restoreTrade: Restores a soft-deleted trade row
 * - validateInput: Validates trade label format on blur (punish late)
 * - rewardEarly: Clears error styling on input if previously-invalid field is now valid
 * - serialize: Serializes all active trade labels into hidden inputs before submit
 *
 * @example
 * <form data-controller="trades-config">
 *   <div data-trades-config-target="category" data-category="Entreprises">
 *     <span data-trades-config-target="counter">...</span>
 *     <div data-trades-config-target="list">
 *       <div data-trades-config-target="item" data-deleted="false">
 *         <input data-trades-config-target="input" value="01 - Démolition">
 *         <button data-action="trades-config#deleteTrade" data-trades-config-target="deleteButton">
 *         <button data-action="trades-config#restoreTrade" data-trades-config-target="restoreButton" class="hidden">
 *       </div>
 *     </div>
 *     <button data-action="trades-config#addTrade" data-trades-config-target="addButton" data-category="Entreprises">
 *     <p data-trades-config-target="maxMessage" class="hidden">
 *     <input type="hidden" data-trades-config-target="hiddenField" data-category="Entreprises">
 *   </div>
 * </form>
 */
export default class extends Controller {
  static targets = [
    'category',
    'counter',
    'list',
    'addButton',
    'maxMessage',
    'hiddenField',
    'item',
    'input',
    'deleteButton',
    'restoreButton',
  ];

  static values = {
    maxPerCategory: { type: Number, default: 60 },
  };

  /** Regex matching server-side TRADE_LABEL_FORMAT in organization_setting.rb */
  static TRADE_FORMAT = /^\d+[a-z]?\s*-\s*.+$/;

  /** Auto-generated prefix pattern (e.g., "99 - ") — skip validation for these */
  static PREFIX_ONLY = /^\d+[a-z]?\s*-\s*$/;

  connect() {
    this.sortables = [];
    this.invalidCount = 0;
    this.#initSortables();
    // Re-validate all inputs on connect so that after a server 422 re-render,
    // previously invalid inputs are immediately highlighted.
    this.#revalidateAll();
  }

  disconnect() {
    this.sortables.forEach((s) => s.destroy());
    this.sortables = [];
  }

  // ── Actions ──

  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    }
  }

  static ERROR_MESSAGE = 'Format attendu : 01 - Nom du lot';

  /**
   * "Reward early" — on each keystroke, clear the error state as soon as
   * the user fixes a previously-invalid input. New errors are only shown
   * on blur (punish late) to avoid distracting mid-typing highlights.
   */
  rewardEarly(event) {
    const input = event.target;
    if (input.dataset.wasInvalid !== 'true') return;

    const value = input.value.trim();
    if (this.constructor.TRADE_FORMAT.test(value)) {
      this.#clearInputError(input);
      this.invalidCount--;
      input.dataset.wasInvalid = 'false';
    }
  }

  validateInput(event) {
    const input = event.target;

    // Skip readonly inputs (existing persisted lots)
    if (input.readOnly) return;

    // Skip soft-deleted rows
    const item = input.closest('[data-trades-config-target="item"]');
    if (item && item.dataset.deleted === 'true') return;

    const value = input.value.trim();

    // Skip auto-generated prefix-only values (e.g., "99 - ")
    if (this.constructor.PREFIX_ONLY.test(value)) return;

    const isValid = this.constructor.TRADE_FORMAT.test(value);

    if (isValid) {
      this.#clearInputError(input);
      if (input.dataset.wasInvalid === 'true') {
        this.invalidCount--;
        input.dataset.wasInvalid = 'false';
      }
    } else {
      this.#showInputError(input);
      if (input.dataset.wasInvalid !== 'true') {
        this.invalidCount++;
        input.dataset.wasInvalid = 'true';
      }
    }
  }

  addTrade(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const categoryName = button.dataset.category;
    const list = this.#listForCategory(categoryName);
    if (!list) return;

    const activeCount = this.#activeItemCount(list);
    if (activeCount >= this.maxPerCategoryValue) return;

    // Determine next lot number prefix
    const nextNumber = this.#nextLotNumber(list);
    const prefix = `${String(nextNumber).padStart(2, '0')} - `;

    // Clone an existing row as template, or build from scratch
    const row = this.#buildTradeRow(prefix, list);
    list.appendChild(row);

    // Focus the new input
    const input = row.querySelector('[data-trades-config-target="input"]');
    if (input) {
      input.focus();
      input.setSelectionRange(prefix.length, prefix.length);
    }

    this.#updateCategoryState(categoryName);
  }

  deleteTrade(event) {
    event.preventDefault();
    const item = event.currentTarget.closest('[data-trades-config-target="item"]');
    if (!item) return;

    const input = item.querySelector('[data-trades-config-target="input"]');
    const isPersisted = input && input.readOnly;
    const categoryName = this.#categoryNameForItem(item);

    // New (unsaved) lots: remove from DOM immediately
    if (!isPersisted) {
      if (input && input.dataset.wasInvalid === 'true') {
        this.invalidCount--;
      }
      item.remove();
      if (categoryName) this.#updateCategoryState(categoryName);
      return;
    }

    // Persisted lots: soft-delete (strikethrough + restore button)
    item.dataset.deleted = 'true';
    item.classList.add('line-through', 'opacity-50');

    // Disable drag handle
    const handle = item.querySelector('.drag-handle');
    if (handle) {
      handle.classList.add('pointer-events-none', 'opacity-30');
    }

    // Swap delete/restore button visibility
    const deleteBtn = item.querySelector('[data-trades-config-target="deleteButton"]');
    const restoreBtn = item.querySelector('[data-trades-config-target="restoreButton"]');
    if (deleteBtn) deleteBtn.classList.add('hidden');
    if (restoreBtn) restoreBtn.classList.remove('hidden');

    // Disable input and clear validation state
    if (input) {
      input.disabled = true;
      if (input.dataset.wasInvalid === 'true') {
        this.invalidCount--;
        input.dataset.wasInvalid = 'false';
        this.#clearInputError(input);
      }
    }

    if (categoryName) this.#updateCategoryState(categoryName);
  }

  restoreTrade(event) {
    event.preventDefault();
    const item = event.currentTarget.closest('[data-trades-config-target="item"]');
    if (!item) return;

    item.dataset.deleted = 'false';
    item.classList.remove('line-through', 'opacity-50');

    // Re-enable drag handle
    const handle = item.querySelector('.drag-handle');
    if (handle) {
      handle.classList.remove('pointer-events-none', 'opacity-30');
    }

    // Swap restore/delete button visibility
    const deleteBtn = item.querySelector('[data-trades-config-target="deleteButton"]');
    const restoreBtn = item.querySelector('[data-trades-config-target="restoreButton"]');
    if (deleteBtn) deleteBtn.classList.remove('hidden');
    if (restoreBtn) restoreBtn.classList.add('hidden');

    // Re-enable input and re-validate
    const input = item.querySelector('[data-trades-config-target="input"]');
    if (input) {
      input.disabled = false;
      this.validateInput({ target: input });
    }

    const categoryName = this.#categoryNameForItem(item);
    if (categoryName) this.#updateCategoryState(categoryName);
  }

  serialize(event) {
    // Re-validate all active inputs before serializing
    this.#revalidateAll();

    if (this.invalidCount > 0) {
      event.preventDefault();
      this.#showToast('Corrigez les lots en erreur avant d\u2019enregistrer');

      const firstInvalid = this.element.querySelector(
        '[data-trades-config-target="input"][data-was-invalid="true"]'
      );
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid.focus();
      }
      return;
    }

    // Serialize each category's active trade labels into hidden inputs
    this.categoryTargets.forEach((category) => {
      const categoryName = category.dataset.category;
      const list = category.querySelector('[data-trades-config-target="list"]');
      if (!list) return;

      const labels = [];
      const items = list.querySelectorAll('[data-trades-config-target="item"]');
      items.forEach((item) => {
        if (item.dataset.deleted !== 'true') {
          const input = item.querySelector('[data-trades-config-target="input"]');
          if (input && input.value.trim()) {
            labels.push(input.value.trim());
          }
        }
      });

      const hiddenField = this.hiddenFieldTargets.find((f) => f.dataset.category === categoryName);
      if (hiddenField) {
        hiddenField.value = JSON.stringify(labels);
      }
    });
  }

  // ── Private ──

  #initSortables() {
    this.listTargets.forEach((list) => {
      const sortable = Sortable.create(list, {
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'opacity-30',
        filter: '[data-deleted="true"]',
        onMove: (evt) => evt.related.dataset.deleted !== 'true',
      });
      this.sortables.push(sortable);
    });
  }

  #listForCategory(categoryName) {
    const category = this.categoryTargets.find((c) => c.dataset.category === categoryName);
    if (!category) return null;
    return category.querySelector('[data-trades-config-target="list"]');
  }

  #activeItemCount(list) {
    const items = list.querySelectorAll('[data-trades-config-target="item"]');
    let count = 0;
    items.forEach((item) => {
      if (item.dataset.deleted !== 'true') count++;
    });
    return count;
  }

  #nextLotNumber(list) {
    let maxNumber = 0;
    const items = list.querySelectorAll('[data-trades-config-target="item"]');
    items.forEach((item) => {
      const input = item.querySelector('[data-trades-config-target="input"]');
      if (input) {
        const match = input.value.match(/^(\d+)/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNumber) maxNumber = num;
        }
      }
    });
    return maxNumber + 1;
  }

  #buildTradeRow(defaultValue, list) {
    // Clone the first existing row as a template if available
    const existingItem = list.querySelector('[data-trades-config-target="item"]');
    if (existingItem) {
      const clone = existingItem.cloneNode(true);
      clone.dataset.deleted = 'false';
      clone.classList.remove('line-through', 'opacity-50');

      const input = clone.querySelector('[data-trades-config-target="input"]');
      if (input) {
        input.value = defaultValue;
        input.disabled = false;
        input.removeAttribute('readonly');
        input.removeAttribute('title');
        input.dataset.wasInvalid = 'false';
        input.className =
          'flex-1 min-w-0 px-2 py-1 text-sm text-neutral-900 border border-transparent rounded focus-visible:border-primary-300 focus-visible:ring-2 focus-visible:ring-primary-500 hover:border-neutral-300 bg-transparent';
        input.setAttribute(
          'data-action',
          'keydown->trades-config#handleKeydown blur->trades-config#validateInput input->trades-config#rewardEarly'
        );
      }

      const handle = clone.querySelector('.drag-handle');
      if (handle) {
        handle.classList.remove('pointer-events-none', 'opacity-30');
      }

      const deleteBtn = clone.querySelector('[data-trades-config-target="deleteButton"]');
      if (deleteBtn) deleteBtn.classList.remove('hidden');

      const restoreBtn = clone.querySelector('[data-trades-config-target="restoreButton"]');
      if (restoreBtn) restoreBtn.classList.add('hidden');

      return clone;
    }

    // Fallback: build from scratch using DOM API (no innerHTML)
    const row = document.createElement('div');
    row.className =
      'flex flex-wrap items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-neutral-50 group/item';
    row.setAttribute('data-trades-config-target', 'item');
    row.dataset.deleted = 'false';

    // Drag handle
    const handle = document.createElement('span');
    handle.className =
      'drag-handle cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-600 flex-shrink-0';
    handle.setAttribute('aria-label', 'Réordonner');
    handle.textContent = '⠿';

    // Text input
    const input = document.createElement('input');
    input.type = 'text';
    input.value = defaultValue;
    input.className =
      'flex-1 min-w-0 px-2 py-1 text-sm text-neutral-900 border border-transparent rounded focus-visible:border-primary-300 focus-visible:ring-2 focus-visible:ring-primary-500 hover:border-neutral-300 bg-transparent';
    input.setAttribute('data-trades-config-target', 'input');
    input.setAttribute(
      'data-action',
      'keydown->trades-config#handleKeydown blur->trades-config#validateInput'
    );
    input.setAttribute('aria-label', 'Nom du lot');

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className =
      'flex-shrink-0 p-1 text-neutral-400 hover:text-danger-600 rounded transition-colors focus-visible:ring-2 focus-visible:ring-danger-500 focus-visible:ring-offset-1';
    deleteBtn.setAttribute('data-action', 'trades-config#deleteTrade');
    deleteBtn.setAttribute('data-trades-config-target', 'deleteButton');
    deleteBtn.setAttribute('aria-label', 'Supprimer ce lot');
    deleteBtn.textContent = '✕';

    // Restore button (hidden)
    const restoreBtn = document.createElement('button');
    restoreBtn.type = 'button';
    restoreBtn.className =
      'hidden flex-shrink-0 p-1 text-neutral-400 hover:text-success-600 rounded transition-colors focus-visible:ring-2 focus-visible:ring-success-500 focus-visible:ring-offset-1';
    restoreBtn.setAttribute('data-action', 'trades-config#restoreTrade');
    restoreBtn.setAttribute('data-trades-config-target', 'restoreButton');
    restoreBtn.setAttribute('aria-label', 'Restaurer ce lot');
    restoreBtn.textContent = '↩';

    row.appendChild(handle);
    row.appendChild(input);
    row.appendChild(deleteBtn);
    row.appendChild(restoreBtn);

    return row;
  }

  #categoryNameForItem(item) {
    const category = item.closest('[data-trades-config-target="category"]');
    return category ? category.dataset.category : null;
  }

  #updateCategoryState(categoryName) {
    const list = this.#listForCategory(categoryName);
    if (!list) return;

    const activeCount = this.#activeItemCount(list);

    // Update counter badge
    const category = this.categoryTargets.find((c) => c.dataset.category === categoryName);
    if (category) {
      const counter = category.querySelector('[data-trades-config-target="counter"]');
      if (counter) {
        // Update the badge text content (find the innermost text node in the badge)
        const badge = counter.querySelector('span') || counter;
        badge.textContent = String(activeCount);
      }
    }

    // Update add button + max message
    const addButton = this.addButtonTargets.find((b) => b.dataset.category === categoryName);
    const maxMessage = this.#maxMessageForCategory(categoryName);

    const atMax = activeCount >= this.maxPerCategoryValue;
    if (addButton) addButton.disabled = atMax;
    if (maxMessage) maxMessage.classList.toggle('hidden', !atMax);
  }

  #revalidateAll() {
    this.inputTargets.forEach((input) => {
      this.validateInput({ target: input });
    });
  }

  #showToast(message) {
    const container = document.getElementById('flash-container');
    if (!container) return;

    const toast = document.createElement('div');
    toast.setAttribute('data-flash-target', 'message');
    toast.className =
      'bg-danger-50 border-danger-200 text-danger-800 px-6 py-4 rounded-lg shadow-lg border max-w-md transform transition-all duration-300 ease-in-out';
    toast.textContent = message;

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.setAttribute('data-action', 'click->flash#dismiss');
    dismiss.className = 'ml-4 text-danger-600 hover:text-danger-800 font-bold';
    dismiss.textContent = '✕';

    toast.appendChild(dismiss);
    container.appendChild(toast);
  }

  #maxMessageForCategory(categoryName) {
    const category = this.categoryTargets.find((c) => c.dataset.category === categoryName);
    if (!category) return null;
    return category.querySelector('[data-trades-config-target="maxMessage"]');
  }

  #showInputError(input) {
    input.classList.remove('border-transparent', 'bg-transparent');
    input.classList.add('border-danger-500', 'bg-danger-50');

    const item = input.closest('[data-trades-config-target="item"]');
    if (!item || item.querySelector('.trades-config-error')) return;

    const errorId = `error-${crypto.randomUUID().slice(0, 8)}`;
    const error = document.createElement('p');
    error.id = errorId;
    error.className = 'trades-config-error w-full text-danger-600 text-xs -mt-1 ml-8';
    error.textContent = this.constructor.ERROR_MESSAGE;
    error.setAttribute('role', 'alert');
    item.appendChild(error);
    input.setAttribute('aria-describedby', errorId);
    input.setAttribute('aria-invalid', 'true');
  }

  #clearInputError(input) {
    input.classList.remove('border-danger-500', 'bg-danger-50');
    input.classList.add('border-transparent', 'bg-transparent');
    input.removeAttribute('aria-describedby');
    input.removeAttribute('aria-invalid');

    const item = input.closest('[data-trades-config-target="item"]');
    if (!item) return;

    const error = item.querySelector('.trades-config-error');
    if (error) error.remove();
  }
}
